// src/components/About.js
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../LanguageContext.js';

const About = () => {
  const { t } = useLanguage();
  const [visibleElements, setVisibleElements] = useState({});

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleElements(prev => ({
            ...prev,
            [entry.target.dataset.id]: true
          }));
        }
      });
    }, observerOptions);

    document.querySelectorAll('[data-observe]').forEach(element => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" style={{ backgroundColor: '#FFFFFF' }} className="relative overflow-hidden">
      
      {/* Section 1: Chi Sono - Image Left, Text Right */}
      <div className="relative w-full flex flex-col md:flex-row items-center overflow-hidden">
        {/* Left - Image */}
        <div 
          className="w-full md:w-1/2 h-96 md:h-screen flex items-center justify-center order-2 md:order-1"
          style={{ backgroundColor: '#FFFFFF' }}
          data-observe
          data-id="about-image-1"
        >
          <div 
            className={`w-5/6 md:w-11/12 h-5/6 overflow-hidden relative group transition-all duration-700 ${
              visibleElements['about-image-1'] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <img 
              src="/images/DHOME_ABOUT_1.webp" 
              alt="" 
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </div>

        {/* Right - Text */}
        <div 
          className="w-full md:w-1/2 min-h-96 md:h-screen flex items-center justify-center px-6 md:px-12 py-12 md:py-0 order-1 md:order-2"
          style={{ backgroundColor: '#F5F5F3' }}
          data-observe
          data-id="about-text-1"
        >
          <div 
            className={`max-w-lg w-full space-y-8 transition-all duration-700 ${
              visibleElements['about-text-1'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div>
              <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#585856' }}>
                {t('about', 'section_subtitle')}
              </p>
              <h2 
                className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] tracking-tight mb-6"
                style={{ color: '#000000' }}
              >
                {t('about', 'section_title')}
              </h2>
              <div className="w-16 h-1 mb-8" style={{ backgroundColor: '#000000' }}></div>
              <p 
                className="text-sm md:text-base leading-relaxed"
                style={{ color: '#585856', fontWeight: 300, lineHeight: 1.7 }}
              >
                {t('about', 'about_intro')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Il Mio Metodo - Text Left, Image Right */}
      <div className="relative w-full flex flex-col md:flex-row items-center overflow-hidden">
        {/* Left - Text */}
        <div 
          className="w-full md:w-1/2 min-h-96 md:h-screen flex items-center justify-center px-6 md:px-12 py-12 md:py-0"
          style={{ backgroundColor: '#F5F5F3' }}
          data-observe
          data-id="about-text-2"
        >
          <div 
            className={`max-w-lg w-full space-y-8 transition-all duration-700 ${
              visibleElements['about-text-2'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div>
              <h3 
                className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] tracking-tight mb-8"
                style={{ color: '#000000' }}
              >
                {t('about', 'mission_title')}
              </h3>
              <div className="w-16 h-1 mb-8" style={{ backgroundColor: '#000000' }}></div>
              <p 
                className="text-sm md:text-base leading-relaxed mb-6"
                style={{ color: '#585856', fontWeight: 300, lineHeight: 1.7 }}
              >
                {t('about', 'mission_text')}
              </p>
            </div>
          </div>
        </div>

        {/* Right - Image */}
        <div 
          className="w-full md:w-1/2 h-96 md:h-screen flex items-center justify-center"
          style={{ backgroundColor: '#FFFFFF' }}
          data-observe
          data-id="about-image-2"
        >
          <div 
            className={`w-5/6 md:w-11/12 h-5/6 overflow-hidden relative group transition-all duration-700 ${
              visibleElements['about-image-2'] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <img 
              src="/images/DHOME_ABOUT_2.webp" 
              alt="" 
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </div>
      </div>

      {/* Section 3: Cosa Faccio */}
      <div style={{ backgroundColor: '#FFFFFF' }} className="py-20 md:py-32">
        <div className="container mx-auto px-6">
          <div 
            className="max-w-2xl mx-auto text-center mb-16 md:mb-24"
            data-observe
            data-id="services-title"
          >
            <h2 
              className={`text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] tracking-tight transition-all duration-700 ${
                visibleElements['services-title'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ color: '#000000' }}
            >
              {t('about', 'sectors_title')}
            </h2>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: t('about', 'sector1_title'),
                description: t('about', 'sector1_desc'),
                icon: '🏡',
                accent: '#D4A574'
              },
              {
                title: t('about', 'sector2_title'),
                description: t('about', 'sector2_desc'),
                icon: '💳',
                accent: '#8B8B7A'
              },
              {
                title: t('about', 'sector3_title'),
                description: t('about', 'sector3_desc'),
                icon: '📊',
                accent: '#A89968'
              }
            ].map((service, idx) => (
              <div 
                key={idx}
                className="transition-all duration-300 hover:shadow-lg"
                data-observe
                data-id={`service-card-${idx}`}
              >
                <div 
                  className={`h-full p-8 md:p-10 rounded-lg transition-all duration-700 ${
                    visibleElements[`service-card-${idx}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ 
                    backgroundColor: '#F5F5F3',
                    borderTop: `3px solid ${service.accent}`
                  }}
                >
                  <div className="text-4xl mb-6 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 
                    className="text-2xl md:text-3xl font-black leading-[1.1] mb-4"
                    style={{ color: '#000000' }}
                  >
                    {service.title}
                  </h3>
                  <p 
                    className="text-sm md:text-base leading-relaxed"
                    style={{ color: '#585856', fontWeight: 300, lineHeight: 1.7 }}
                  >
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section 4: Come Funziona - Process */}
      <div style={{ backgroundColor: '#F5F5F3' }} className="py-20 md:py-32">
        <div className="container mx-auto px-6">
          <div 
            className="max-w-2xl mx-auto text-center mb-16 md:mb-24"
            data-observe
            data-id="process-title"
          >
            <h2 
              className={`text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] tracking-tight transition-all duration-700 ${
                visibleElements['process-title'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ color: '#000000' }}
            >
              {t('about', 'process_title')}
            </h2>
          </div>

          {/* Process Steps */}
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              {[
                {
                  step: '01',
                  title: t('about', 'process_step1_title'),
                  description: t('about', 'process_step1_desc')
                },
                {
                  step: '02',
                  title: t('about', 'process_step2_title'),
                  description: t('about', 'process_step2_desc')
                },
                {
                  step: '03',
                  title: t('about', 'process_step3_title'),
                  description: t('about', 'process_step3_desc')
                },
                {
                  step: '04',
                  title: t('about', 'process_step4_title'),
                  description: t('about', 'process_step4_desc')
                }
              ].map((step, idx) => (
                <div 
                  key={idx}
                  className="transition-all duration-300"
                  data-observe
                  data-id={`process-step-${idx}`}
                >
                  <div 
                    className={`transition-all duration-700 ${
                      visibleElements[`process-step-${idx}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                  >
                    <div 
                      className="text-6xl md:text-7xl font-black mb-4"
                      style={{ color: '#D4A574', opacity: 0.2 }}
                    >
                      {step.step}
                    </div>
                    <h3 
                      className="text-2xl md:text-3xl font-black leading-[1.1] mb-3"
                      style={{ color: '#000000' }}
                    >
                      {step.title}
                    </h3>
                    <p 
                      className="text-sm md:text-base leading-relaxed"
                      style={{ color: '#585856', fontWeight: 300, lineHeight: 1.7 }}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Gradient transition */}
      <div 
        className="relative h-8 z-20 pointer-events-none w-full"
        style={{
          background: 'linear-gradient(to bottom, rgba(245, 245, 243, 0) 0%, rgba(245, 245, 243, 1) 100%)'
        }}
      ></div>
    </section>
  );
};

export default About;